/**
 * Phase 9 — Bilingual copy for the /v2 landing sections ONLY.
 * Routes and invariant contact data live outside this file (see
 * v2LandingContactChannels below).
 * Repeated items carry stable IDs — never use translated text as a key.
 */

export const v2LandingContactChannels = {
  email:     { value: 'illumi.dev@outlook.com', href: 'mailto:illumi.dev@outlook.com' },
  phone:     { value: '+1 (615) 555-0100',      href: 'tel:+16155550100' },
  instagram: { value: '@illumi.dev',            href: 'https://instagram.com/illumi.dev' },
};

export const v2LandingCopy = {
  en: {
    hero: {
      eyebrow: 'NASHVILLE · BILINGUAL BY DESIGN',
      title_lines: ['Clarity, shipped.', 'Systems that make the business easier to run.'],
      lede: 'illumi.dev is a Nashville-based digital business consultancy building bilingual systems, workflows, and internal tools that help small operations grow with intention.',
      primary_cta: 'Request an audit →',
      badges: ['Nashville-based.', 'Bilingual by design.', 'Business outcomes first.'],
    },
    problem: {
      eyebrow: 'The problem',
      headline: "Your business isn't broken.",
      emphasis: 'It may need a clearer system.',
      body: "Most operations slow down not because the team is failing, but because tools, handoffs, and information don't move together. We look at the whole system, not just the surface.",
    },
    process: {
      eyebrow: 'Working process',
      headline: 'Diagnose → Build → Improve',
      lede: 'A simple three-step arc we apply to every engagement.',
      steps: [
        { id: 'diagnose', label: 'Diagnose', body: 'Understand the current workflow, identify root causes, review evidence, establish priorities, and define success.' },
        { id: 'build',    label: 'Build',    body: 'Design and implement the appropriate process, configuration, automation, integration, or business platform.' },
        { id: 'improve',  label: 'Improve',  body: 'Measure adoption, refine the workflow, strengthen documentation, and identify the next responsible improvement.' },
      ],
    },
    whoWeHelp: {
      eyebrow: 'Who we help',
      headline: 'illumi.dev partners with:',
      items: [
        { id: 'founders',            label: 'Founders',           body: 'Turning an idea into a credible product, offer, or digital presence.' },
        { id: 'small-teams',         label: 'Small Teams',        body: 'Making important digital decisions without a full internal product group.' },
        { id: 'growing-businesses',  label: 'Growing Businesses', body: 'Modernizing customer experiences while protecting what already works.' },
        { id: 'special-projects',    label: 'Special Projects',   body: 'Launching a new service, platform, campaign, or operational system.' },
      ],
    },
    whyIllumi: {
      eyebrow: 'Why illumi.dev',
      headline: 'A practical way to build with intention.',
      principles: [
        { id: 'strategy-first',   text: 'Strategy before technology' },
        { id: 'practical',        text: 'Practical implementation' },
        { id: 'business-and-tech',text: 'Business and technology together' },
        { id: 'bilingual',        text: 'Bilingual by design' },
        { id: 'responsible-ai',   text: 'Responsible AI' },
        { id: 'adoption',         text: 'Systems built for adoption' },
        { id: 'honest-scope',     text: 'Honest scope and value' },
      ],
      note_label: 'Responsible AI',
      note_body: 'Applied where it helps, kept out of decisions that require human judgment. Documented so the team knows what it does and does not do.',
    },
    selectedSystems: {
      eyebrow: 'Selected systems',
      headline: 'Example System building blocks.',
      lede: 'Illustrative examples of the operational systems we design and ship. Every engagement is shaped to the business.',
      systems: [
        { id: 'lead-intake',        title: 'Lead intake and routing',   body: 'Structured inbound capture with routing rules, response SLAs, and clean handoff to the sales workflow — no lost inquiries.' },
        { id: 'client-onboarding',  title: 'Client onboarding',         body: 'Guided intake, document collection, kickoff scheduling, and status visibility so new clients feel oriented and progress is legible.' },
        { id: 'scheduling',         title: 'Scheduling and confirmations', body: 'Availability logic, confirmations, bilingual reminders, and no-show recovery — reducing back-and-forth for both sides.' },
        { id: 'bilingual-comms',    title: 'Bilingual communications',  body: 'English and Spanish message templates, forms, and confirmations designed as equal experiences rather than translations.' },
      ],
    },
    auditCta: {
      eyebrow: 'Business Systems Audit',
      headline: 'Start with clarity.',
      body: 'A focused review of your current systems, operational friction, and priorities, resulting in a practical 90-day action plan.',
      cta_label: 'Request your audit →',
    },
    about: {
      eyebrow: 'About illumi.dev',
      headline: 'Built by one operator, close to the work.',
      body: 'illumi.dev is led by Sergio Delgado, a bilingual digital business consultant based in Nashville. Every engagement is scoped, executed, and reviewed by the same person from start to finish.',
      name: 'Sergio Delgado',
      role: 'Digital Business Consultant · Nashville',
    },
    finalCta: {
      eyebrow: 'Get in touch',
      headline: 'Ready when you are.',
      body: 'Send a note or request the audit — whichever fits where you are.',
      contacts: [
        { id: 'email',     label: 'Email' },
        { id: 'phone',     label: 'Phone' },
        { id: 'instagram', label: 'Instagram' },
      ],
    },
  },
  es: {
    hero: {
      eyebrow: 'NASHVILLE · BILINGÜE POR DISEÑO',
      title_lines: ['Claridad, hecha realidad.', 'Sistemas que facilitan la operación de tu negocio.'],
      lede: 'illumi.dev es una consultoría digital con sede en Nashville que crea sistemas, flujos de trabajo y herramientas internas bilingües para ayudar a los pequeños negocios a crecer con intención.',
      primary_cta: 'Solicitar una auditoría →',
      badges: ['Con base en Nashville.', 'Bilingüe por diseño.', 'Los resultados del negocio van primero.'],
    },
    problem: {
      eyebrow: 'El problema',
      headline: 'Tu negocio no está roto.',
      emphasis: 'Puede que solo necesite un sistema más claro.',
      body: 'La mayoría de las operaciones no se frenan porque el equipo esté fallando, sino porque las herramientas, los traspasos entre personas y áreas, y la información no están conectados. Analizamos el sistema completo, no solo lo que se ve en la superficie.',
    },
    process: {
      eyebrow: 'Cómo trabajamos',
      headline: 'Diagnosticar → Construir → Mejorar',
      lede: 'Un proceso sencillo de tres etapas que aplicamos en cada proyecto.',
      steps: [
        { id: 'diagnose', label: 'Diagnosticar', body: 'Entender el flujo actual, identificar causas raíz, revisar evidencia, establecer prioridades y definir el éxito.' },
        { id: 'build',    label: 'Construir',    body: 'Diseñar e implementar el proceso, la configuración, la automatización, la integración o la plataforma de negocio adecuados.' },
        { id: 'improve',  label: 'Mejorar',      body: 'Medir la adopción, refinar el flujo, fortalecer la documentación e identificar la siguiente mejora responsable.' },
      ],
    },
    whoWeHelp: {
      eyebrow: 'A quién ayudamos',
      headline: 'illumi.dev trabaja con:',
      items: [
        { id: 'founders',            label: 'Fundadores',            body: 'Convertir una idea en un producto, oferta o presencia digital creíble.' },
        { id: 'small-teams',         label: 'Equipos pequeños',      body: 'Tomar decisiones digitales importantes sin contar con un área de producto interna completa.' },
        { id: 'growing-businesses',  label: 'Negocios en crecimiento', body: 'Modernizar la experiencia del cliente sin romper lo que ya funciona.' },
        { id: 'special-projects',    label: 'Proyectos especiales',  body: 'Lanzar un nuevo servicio, plataforma, campaña o sistema operativo.' },
      ],
    },
    whyIllumi: {
      eyebrow: 'Por qué illumi.dev',
      headline: 'Una forma práctica de construir con intención.',
      principles: [
        { id: 'strategy-first',   text: 'Estrategia antes que tecnología' },
        { id: 'practical',        text: 'Implementación práctica' },
        { id: 'business-and-tech',text: 'Negocio y tecnología en conjunto' },
        { id: 'bilingual',        text: 'Bilingüe por diseño' },
        { id: 'responsible-ai',   text: 'IA responsable' },
        { id: 'adoption',         text: 'Sistemas diseñados para que el equipo los adopte' },
        { id: 'honest-scope',     text: 'Alcance y valor con transparencia' },
      ],
      note_label: 'IA responsable',
      note_body: 'Aplicada donde aporta valor y mantenida fuera de las decisiones que requieren criterio humano. Documentada para que el equipo sepa qué hace y qué no hace.',
    },
    selectedSystems: {
      eyebrow: 'Sistemas seleccionados',
      headline: 'Ejemplos de componentes para sistemas.',
      lede: 'Ejemplos ilustrativos de los sistemas para operaciones que diseñamos y ponemos en marcha. Cada proyecto se adapta al negocio.',
      systems: [
        { id: 'lead-intake',        title: 'Captación y asignación de prospectos', body: 'Captura estructurada de solicitudes, reglas de asignación, tiempos de respuesta y una transición clara al proceso de ventas, sin dejar consultas sin atender.' },
        { id: 'client-onboarding',  title: 'Incorporación de clientes',            body: 'Recepción guiada, recopilación de documentos, programación de la reunión inicial y visibilidad del estado para que cada nuevo cliente sepa qué sigue y el avance sea claro.' },
        { id: 'scheduling',         title: 'Programación y confirmaciones',        body: 'Reglas de disponibilidad, confirmaciones, recordatorios bilingües y seguimiento de citas perdidas, reduciendo el intercambio innecesario para ambas partes.' },
        { id: 'bilingual-comms',    title: 'Comunicaciones bilingües',             body: 'Plantillas, formularios y confirmaciones en inglés y español diseñados como experiencias equivalentes, no como simples traducciones.' },
      ],
    },
    auditCta: {
      eyebrow: 'Auditoría de Sistemas de Negocio',
      headline: 'Comienza con claridad.',
      body: 'Una revisión enfocada de tus sistemas actuales, la fricción operativa y las prioridades, con un plan de acción práctico para los próximos 90 días.',
      cta_label: 'Solicita tu auditoría →',
    },
    about: {
      eyebrow: 'Sobre illumi.dev',
      headline: 'Creado por una sola persona, cerca del trabajo real.',
      body: 'illumi.dev está dirigido por Sergio Delgado, consultor digital bilingüe con sede en Nashville. Cada proyecto es definido, ejecutado y revisado por la misma persona de principio a fin.',
      name: 'Sergio Delgado',
      role: 'Consultor de Negocios Digitales · Nashville',
    },
    finalCta: {
      eyebrow: 'Contacto',
      headline: 'Aquí estamos cuando quieras comenzar.',
      body: 'Envíanos un mensaje o solicita una auditoría, según lo que mejor se ajuste a tu situación actual.',
      contacts: [
        { id: 'email',     label: 'Correo' },
        { id: 'phone',     label: 'Teléfono' },
        { id: 'instagram', label: 'Instagram' },
      ],
    },
  },
};

// Dev-only missing-key warning, deduped by keyPath.
const _warned = new Set();
export function getLandingCopy(lang, sectionKey) {
  const L = lang === 'es' ? 'es' : 'en';
  const bucket = v2LandingCopy[L] || v2LandingCopy.en;
  const section = bucket[sectionKey];
  if (section === undefined) {
    if (process.env.NODE_ENV !== 'production' && !_warned.has(`${L}:${sectionKey}`)) {
      _warned.add(`${L}:${sectionKey}`);
      // eslint-disable-next-line no-console
      console.warn(`[v2LandingCopy] missing section "${sectionKey}" for lang "${L}" — falling back to en`);
    }
    return v2LandingCopy.en[sectionKey] || {};
  }
  return section;
}
